import SeasonSection from './devTools/SeasonSection'
import ReplicaSection from './devTools/ReplicaSection'
import PipelineSection from './devTools/PipelineSection'
import LocalSection from './devTools/LocalSection'
import { hasAnyDevTool } from '../../devToolCapabilities'
import type { DataMode } from '../../dataMode'

interface GameweekDevToolsProps {
  /** Whether the signed-in user is the admin. Only the admin sees the
   *  dev-tools rail; non-admins get no handle, rail, or controls. */
  isAdmin: boolean
  /** Which world the server is running in. Decides which controls exist —
   *  null until `/me` lands, which renders no rail rather than a wrong one. */
  mode: DataMode | null
  /** The fixture-dependent controls are hidden when the gameweek has no games. */
  hasGames: boolean
  /** Open state is owned by the grid (see `useDevToolsRail`), not this component. */
  devToolsOpen: boolean
  setDevToolsOpen: (open: boolean | ((open: boolean) => boolean)) => void
  resultsRevealed: boolean
  isSimulatingGameweek: boolean
  onSimulateDeadline: () => void
  onToggleResults: () => void
  onFinalizeGameweek: () => void
  onSimulateSeason: () => void
  onDevClearRevealSeen: () => void
  onSimulateGameweekRealtime?: () => void
  onDevChangeGameweek: (delta: number) => void
  onResetAll: (confirm?: string) => void
}

/**
 * The admin-only dev rail,
 * rewired onto the per-tool capability model.
 *
 * It renders in **all three** modes now. Before, one boolean removed the handle, the rail and all
 * nine controls together the moment real football was the source of truth — which also removed the
 * football status panel, the one thing built specifically to be read on a live matchday. What each
 * mode may do is a per-tool question, answered by `devToolCapabilities` and enforced independently
 * by the server.
 *
 * Sections rather than one flat list, and each section returns null when it has nothing available.
 */
function GameweekDevTools({
  isAdmin,
  mode,
  hasGames,
  devToolsOpen,
  setDevToolsOpen,
  resultsRevealed,
  isSimulatingGameweek,
  onSimulateDeadline,
  onToggleResults,
  onFinalizeGameweek,
  onSimulateSeason,
  onDevClearRevealSeen,
  onSimulateGameweekRealtime,
  onDevChangeGameweek,
  onResetAll,
}: GameweekDevToolsProps) {
  if (!isAdmin || !hasAnyDevTool(mode)) return null

  return (
    <>
      <div
        className={`gameweek-grid__dev-backdrop ${
          devToolsOpen ? 'gameweek-grid__dev-backdrop--open' : ''
        }`}
        onClick={() => setDevToolsOpen(false)}
        aria-hidden="true"
      />
      <div
        className={`gameweek-grid__dev-rail ${
          devToolsOpen ? 'gameweek-grid__dev-rail--open' : ''
        }`}
      >
        <button
          type="button"
          className="gameweek-grid__dev-handle"
          onClick={() => setDevToolsOpen((open) => !open)}
          aria-expanded={devToolsOpen}
          aria-label="Toggle dev tools"
          title="Dev tools"
        >
          🛠
        </button>
        <div className="gameweek-grid__dev-panel" aria-hidden={!devToolsOpen}>
          <p className="gameweek-grid__dev-tools-label label-section">Dev tools</p>
          {/* Which world you are in is the precondition for every other judgement made at this
              rail, and until now the only way to tell was to notice which buttons were missing. */}
          <p className={`gameweek-grid__dev-mode gameweek-grid__dev-mode--${mode}`}>
            mode: <strong>{mode}</strong>
          </p>
          <SeasonSection
            mode={mode}
            hasGames={hasGames}
            resultsRevealed={resultsRevealed}
            isSimulatingGameweek={isSimulatingGameweek}
            onSimulateDeadline={onSimulateDeadline}
            onToggleResults={onToggleResults}
            onFinalizeGameweek={onFinalizeGameweek}
            onSimulateSeason={onSimulateSeason}
            onSimulateGameweekRealtime={onSimulateGameweekRealtime}
            onDevChangeGameweek={onDevChangeGameweek}
          />
          <ReplicaSection mode={mode} open={devToolsOpen} />
          <PipelineSection mode={mode} open={devToolsOpen} />
          <LocalSection
            mode={mode}
            hasGames={hasGames}
            onDevClearRevealSeen={onDevClearRevealSeen}
            onResetAll={onResetAll}
          />
        </div>
      </div>
    </>
  )
}

export default GameweekDevTools
